import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { UserPlus, Users as UsersIcon } from 'lucide-react'
import {
  Card, CardHeader, EmptyState, ErrorState, LoadingState,
  NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, addPerson, fetchUsers, formatCount, may, nameIn, translateError,
  type UserRow, type UserStanding,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'
import { Problem, when } from './StockCounts'
import { UserDetail } from './UserDetail'

/* ============================================================================
   W-120 · USERS — everyone who can sign in, and the ones who cannot yet.

   THE THREE TABS ARE THE SCREEN, and "No role" having one of its own is the whole
   argument. Pack 5 says it plainly: it happens to every new joiner in the window
   between being created and having a role assigned, they cannot sign in until somebody
   acts, and it will happen on the first morning of UAT. **A state that is guaranteed to
   occur deserves a place to look for it** — one amber row among thirty-one is not one.

   THE COUNTS ARE DERIVED HERE, not sent. They are counts of rows already on screen, and
   a second source for a number you can see is a number that will one day disagree with
   what it is a count of.

   THE PACK SAYS "Nobody is created here — a person appears when the property system has
   them." **There is no property system in this phase**, which turns that sentence into a
   screen nobody can ever appear on. So there is an "Add a person" button, the source
   column records honestly which rows came that way, and the notice above the table says
   what the arrangement is rather than leaving an empty table looking like a broken sync.
   Change Note 05.
   ========================================================================= */

export function Users() {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const [open, setOpen] = useState<string | null>(null)
  const [tab, setTab] = useState<UserStanding>('ACTIVE')
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [reload, setReload] = useState(0)

  /* GATED ON THE PERMISSION, not merely returned early on it.

     The early return below runs after the hooks, as it must — so without `enabled` the
     screen would ask the server for every user in the hotel before deciding it is not
     allowed to draw them. A 403 in the network log of somebody who never should have
     asked is a small thing that looks exactly like a real one when somebody comes to
     read the logs. */
  const mayAdminister = may('admin.users')

  const board = useQuery({
    queryKey: ['users', reload],
    queryFn: ({ signal }) => fetchUsers({ signal }),
    enabled: mayAdminister,
    retry: false,
  })

  const users = useMemo(() => board.data?.users ?? [], [board.data])

  const counts = useMemo(() => ({
    ACTIVE: users.filter((u) => u.standing === 'ACTIVE').length,
    NO_ROLE: users.filter((u) => u.standing === 'NO_ROLE').length,
    DISABLED: users.filter((u) => u.standing === 'DISABLED').length,
  }), [users])

  const shown = useMemo(() => users.filter((u) => u.standing === tab), [users, tab])

  if (!mayAdminister) return <NoPermissionState />

  if (open !== null)
    return (
      <UserDetail
        userId={open}
        onClose={() => { setOpen(null); setReload((n) => n + 1) }}
      />
    )

  if (board.isLoading && !board.error) return <LoadingState rows={6} />

  if (board.error && !board.data) {
    if (board.error instanceof ApiError && board.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(board.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={board.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  async function create(person: {
    employeeId: string; displayName: string; displayNameAr: string; language: string
  }) {
    setBusy(true); setError(null)
    try {
      const { userId } = await addPerson({
        employeeId: person.employeeId,
        displayName: person.displayName,
        displayNameAr: person.displayNameAr.trim() === '' ? null : person.displayNameAr,
        preferredLanguage: person.language,
      })
      setAdding(false)
      setReload((n) => n + 1)
      // STRAIGHT INTO THE DETAIL, because the person is not finished: they hold no role
      // and cannot sign in, and the next thing anybody wants is the role dropdown.
      setOpen(userId)
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-3.5">
      {error !== null && <Problem error={error} locale={locale} />}

      <Card>
        {/* NOT THE SCREEN'S OWN NAME — the shell says "Users" directly above this. */}
        <CardHeader
          title={t.usrWhoCanSignIn}
          icon={<UsersIcon className="size-4" />}
          actions={
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 rounded-hk bg-[var(--hk-primary)]
                         px-3 py-1.5 text-xs font-medium text-white cursor-pointer"
            >
              <UserPlus className="size-3.5" />
              {t.usrAdd}
            </button>
          }
        />
        <div className="flex flex-col gap-3 p-3.5">
          <p className="max-w-[68ch] text-[12.5px] leading-relaxed text-muted-foreground">
            {t.usrIntro}
          </p>
          {/* SAID OUT LOUD, rather than an empty table that reads as a broken sync. */}
          <p
            className="max-w-[68ch] rounded-hk border border-border bg-muted/40 px-3 py-2
                       text-[12.5px] leading-relaxed text-muted-foreground"
          >
            {t.usrNoPms}
          </p>

          <div className="flex flex-wrap gap-1.5">
            <Tab
              label={t.usrActive} count={counts.ACTIVE} locale={locale}
              active={tab === 'ACTIVE'} onClick={() => setTab('ACTIVE')}
            />
            {/* ITS OWN TAB, and it earns the amber. Somebody here cannot sign in at all
                and nobody outside this screen will ever notice. */}
            <Tab
              label={t.usrNoRole} count={counts.NO_ROLE} locale={locale} tone="warn"
              active={tab === 'NO_ROLE'} onClick={() => setTab('NO_ROLE')}
            />
            <Tab
              label={t.usrDisabled} count={counts.DISABLED} locale={locale}
              active={tab === 'DISABLED'} onClick={() => setTab('DISABLED')}
            />
          </div>
        </div>

        {shown.length === 0 ? (
          <EmptyState title={t.usrNoneHere} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border text-[11.5px] text-muted-foreground">
                  <th className="px-3 py-2 text-start font-medium">{t.usrColPerson}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.usrColEmployeeNo}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.usrColRole}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.usrColProperty}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.usrColLastSeen}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.usrColSource}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {shown.map((user) => (
                  <PersonRow
                    key={user.userId}
                    user={user}
                    locale={locale}
                    onOpen={() => setOpen(user.userId)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {adding && <AddForm busy={busy} onCancel={() => setAdding(false)} onSubmit={create} />}
      </Card>
    </div>
  )
}

function PersonRow({
  user, locale, onOpen,
}: { user: UserRow; locale: 'en' | 'ar'; onOpen: () => void }) {
  const t = STRINGS[locale]
  const role = user.roles[0]

  return (
    <tr className="border-b border-border/60 last:border-0">
      <td className="px-3 py-2">
        <bdi className="font-medium">{nameIn(locale, user.displayName, user.displayNameAr)}</bdi>
        {user.standing === 'NO_ROLE' && (
          <span className="block text-[11px] text-[#b45309] dark:text-[#fbbf24]">
            {t.usrCannotSignIn}
          </span>
        )}
      </td>
      {/* AN EMPLOYEE NUMBER IS A NAME, NOT A QUANTITY — Western digits in both
          languages, like a room number and a count reference. */}
      <td className="px-3 py-2 tnum">{user.employeeId}</td>
      <td className="px-3 py-2">
        {role
          ? <bdi>{nameIn(locale, role.roleName, role.roleNameAr)}</bdi>
          : <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {role
          ? <bdi>{nameIn(locale, role.propertyName, role.propertyNameAr)}</bdi>
          : '—'}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {/* IN WORDS. A dash here would read as "we do not know", and the fact that a new
            joiner has never signed in is exactly what an administrator is looking for. */}
        {user.lastSeenAtUtc === null
          ? <span className="text-[11.5px]">{t.usrNeverSignedIn}</span>
          : when(user.lastSeenAtUtc, locale)}
      </td>
      <td className="px-3 py-2 text-[11.5px] text-muted-foreground">
        {user.source === 'SYNCED' ? t.usrSourceSynced : t.usrSourceEnteredHere}
      </td>
      <td className="px-3 py-2 text-end">
        <button
          type="button"
          onClick={onOpen}
          className="rounded-hk border border-border px-2.5 py-1 text-[11.5px]
                     font-medium hover:bg-muted cursor-pointer"
        >
          {t.usrOpen}
        </button>
      </td>
    </tr>
  )
}

function Tab({
  label, count, locale, active, onClick, tone = 'plain',
}: {
  label: string; count: number; locale: 'en' | 'ar'
  active: boolean; onClick: () => void; tone?: 'plain' | 'warn'
}) {
  const amber = tone === 'warn' && count > 0
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-hk border px-2.5 py-1 text-xs font-medium cursor-pointer
                  ${active
                    ? 'border-[var(--hk-primary)] bg-[var(--hk-primary)] text-white'
                    : amber
                      ? 'border-[#fcd34d] bg-[#fffbeb] text-[#b45309] dark:border-[#92400e] dark:bg-[#2a1f07] dark:text-[#fbbf24]'
                      : 'border-border hover:bg-muted'}`}
    >
      {label} <span className="tnum">{formatCount(count, locale)}</span>
    </button>
  )
}

function AddForm({
  busy, onCancel, onSubmit,
}: {
  busy: boolean
  onCancel: () => void
  onSubmit: (person: {
    employeeId: string; displayName: string; displayNameAr: string; language: string
  }) => void
}) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const [employeeId, setEmployeeId] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [displayNameAr, setDisplayNameAr] = useState('')
  const [language, setLanguage] = useState('en')

  return (
    <div className="flex flex-col gap-2.5 border-t border-border p-3.5">
      <h3 className="text-[13px] font-medium">{t.usrAddTitle}</h3>
      <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t.usrEmployeeNo}>
          {/* NEVER type="number". An employee number is an identifier: it may hold a
              dash, a leading zero, or letters, and a spinner on one is nonsense. */}
          <input
            type="text"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            aria-label={t.usrEmployeeNo}
            className="w-full rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
          />
        </Field>
        <Field label={t.usrName}>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            aria-label={t.usrName}
            className="w-full rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
          />
        </Field>
        <Field label={t.usrNameAr}>
          {/* ALWAYS rtl, WHATEVER LANGUAGE THE SCREEN IS IN. An English-speaking
              administrator typing Arabic into a left-aligned box cannot tell where the
              words have gone. Same rule as W-126 and W-105. */}
          <input
            type="text"
            dir="rtl"
            value={displayNameAr}
            onChange={(e) => setDisplayNameAr(e.target.value)}
            aria-label={t.usrNameAr}
            className="w-full rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
          />
        </Field>
        <Field label={t.usrLanguage}>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            aria-label={t.usrLanguage}
            className="w-full rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
          >
            <option value="en">{t.usrEnglish}</option>
            <option value="ar">{t.usrArabic}</option>
          </select>
        </Field>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy || employeeId.trim() === '' || displayName.trim() === ''}
          onClick={() => onSubmit({ employeeId, displayName, displayNameAr, language })}
          className="rounded-hk bg-[var(--hk-primary)] px-3 py-1.5 text-xs font-medium
                     text-white disabled:opacity-60 cursor-pointer"
        >
          {busy ? t.usrAdding : t.usrCreate}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-hk border border-border px-3 py-1.5 text-xs font-medium
                     hover:bg-muted cursor-pointer"
        >
          {t.usrCancel}
        </button>
      </div>
    </div>
  )
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-[11.5px] text-muted-foreground">
      {label}
      {children}
    </label>
  )
}
